import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { HandleSheet } from "./HandleSheet";

/**
 * Chrome by device (C16). Phone: a 52px top bar with back / title / one right
 * action. Desktop: the brand, four links, @handle, Account. The CSS hides whichever
 * does not apply; both are rendered so the layout never jumps on resize.
 *
 * `foot` is the phone's sticky bar at the bottom of the page. Its height is
 * published as `--foot-h` on <html>, so the bug button (BugButton.tsx) sits
 * above it instead of on its button. Signed in, the page ends with room for
 * that button, so the last row can scroll clear of it.
 */
export function Shell({ title, back, right, active, brand, children, foot }: {
  title?: string | undefined;
  back?: boolean | string | undefined;
  right?: ReactNode;
  active?: "Videos" | "Beta submissions" | "Teams" | "Community" | "Plans" | "Handle" | "Account" | undefined;
  brand?: boolean | undefined;
  children: ReactNode;
  foot?: ReactNode;
}) {
  const nav = useNavigate();
  const { me } = useAuth();
  const [promptDismissed, setPromptDismissed] = useState(false);
  const goBack = () => (typeof back === "string" ? nav(back) : nav(-1));
  const footRef = useRef<HTMLDivElement | null>(null);
  const hasFoot = !!foot;
  useLayoutEffect(() => {
    const el = footRef.current;
    if (!el) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty("--foot-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--foot-h");
    };
  }, [hasFoot]);
  const showHandlePrompt = !promptDismissed && !!(me && (me.handle_prompt || me.user.handle === null));
  const showMnav = !!(me && !back && (brand || active));
  return (
    <div className="page">
      <div className="top">
        {back ? (
          <button className="l" onClick={goBack} aria-label="Back">‹ Back</button>
        ) : brand ? (
          <Link className="brand" to="/">DEEP THIRD</Link>
        ) : (
          <div className="l" />
        )}
        {title ? <div className="t">{title}</div> : null}
        <div className="r">{right ?? (!showMnav && me ? <Link to="/account">Account</Link> : null)}</div>
      </div>
      {showMnav && (
        <div className="mnav" data-testid="mobile-nav" role="navigation" aria-label="Main navigation">
          <Link to="/" className={active === "Videos" ? "on" : ""} aria-current={active === "Videos" ? "page" : undefined}>Videos</Link>
          {me.user.is_operator && <Link to="/admin/beta-submissions" className={active === "Beta submissions" ? "on" : ""} aria-current={active === "Beta submissions" ? "page" : undefined}>Beta submissions</Link>}
          <Link to="/teams" className={active === "Teams" ? "on" : ""} aria-current={active === "Teams" ? "page" : undefined}>
            Teams{me.pending_invites_count ? ` (${me.pending_invites_count})` : ""}
          </Link>
          {me.user.handle && (
            <Link to={`/u/${me.user.handle}`} className={active === "Handle" ? "on" : ""} aria-current={active === "Handle" ? "page" : undefined}>
              @{me.user.handle}
            </Link>
          )}
          <Link to="/account" className={active === "Account" ? "on" : ""} aria-current={active === "Account" ? "page" : undefined}>Account</Link>
        </div>
      )}
      <nav className={`d${me?.user.is_operator ? " operator-nav" : ""}`} aria-label="Main navigation">
        <Link className="brand" to="/">DEEP THIRD</Link>
        <div className="links">
          <Link to="/" className={active === "Videos" ? "on" : ""} aria-current={active === "Videos" ? "page" : undefined}>Videos</Link>
          <Link to="/teams" className={active === "Teams" ? "on" : ""} aria-current={active === "Teams" ? "page" : undefined}>
            Teams{me?.pending_invites_count ? ` (${me.pending_invites_count})` : ""}
          </Link>
          <Link to="/community" className={active === "Community" ? "on" : ""} aria-current={active === "Community" ? "page" : undefined}>Community</Link>
          <Link to="/plans" className={active === "Plans" ? "on" : ""} aria-current={active === "Plans" ? "page" : undefined}>Plans</Link>
        </div>
        <div className="right">
          {me?.user.is_operator && <Link to="/admin/beta-submissions" className={active === "Beta submissions" ? "on" : ""} aria-current={active === "Beta submissions" ? "page" : undefined}>Beta submissions</Link>}
          {me?.user.is_operator && <Link to="/admin/live">Live</Link>}
          {me?.user.is_operator && <Link to="/admin/pipeline">Pipeline</Link>}
          {me?.user.is_operator && <Link to="/admin/invites">Invites</Link>}
          {me?.user.is_operator && <Link to="/admin/bugs">Bugs</Link>}
          {me?.user.handle && (
            <Link to={`/u/${me.user.handle}`} className={active === "Handle" ? "on" : ""} aria-current={active === "Handle" ? "page" : undefined} data-testid="nav-handle">
              @{me.user.handle}
            </Link>
          )}
          {me ? <Link to="/account" className={active === "Account" ? "on" : ""} aria-current={active === "Account" ? "page" : undefined}>Account</Link> : <Link to="/signin">Sign in</Link>}
        </div>
      </nav>
      {children}
      {showHandlePrompt && (
        <HandleSheet
          title="Pick your @handle"
          onClose={() => setPromptDismissed(true)}
          onSaved={() => setPromptDismissed(true)}
        />
      )}
      {me ? <div className="bugroom" aria-hidden="true" /> : null}
      {foot ? <div className="foot" ref={footRef}>{foot}</div> : null}
    </div>
  );
}
